import { api } from "@/util/api";
import { catApi } from "@/util/logging";

export interface EmptyCheckResult {
  isEmpty: boolean;
  filiales: number[];
  description: string;
}

/**
 * AusgabeEmptyCheckModel handles API calls for checking if an Ausgabe is empty
 * and marking it as inactive.
 */
export class AusgabeEmptyCheckModel {
  private static emptyCache = new Map<number, EmptyCheckResult>();

  /**
   * Check if an Ausgabe is empty (no booking data across all filiales)
   * Results are cached to avoid repeated API calls.
   */
  static async isEmptyCheck(ausgabeId: number): Promise<EmptyCheckResult> {
    // Check cache first
    if (this.emptyCache.has(ausgabeId)) {
      const cached = this.emptyCache.get(ausgabeId);
      if (cached) return cached;
    }

    try {
      const path = `/v1/ausgaben/${ausgabeId}/is-empty`;
      const response = await api.get<EmptyCheckResult>(path);
      const data = response.data as EmptyCheckResult;

      // Cache the result
      this.emptyCache.set(ausgabeId, data);

      return data;
    } catch (error) {
      catApi.error(
        `Failed to check if ausgabe ${ausgabeId} is empty:`,
        error as Error
      );
      throw error;
    }
  }

  /**
   * Mark an Ausgabe as inactive. Only succeeds if Ausgabe is truly empty.
   */
  static async markInactive(ausgabeId: number): Promise<void> {
    try {
      const path = `/v1/ausgaben/${ausgabeId}/mark-inactive`;
      await api.post(path, {});

      // Invalidate cache entry after successful marking
      this.emptyCache.delete(ausgabeId);
    } catch (error) {
      catApi.error(
        `Failed to mark ausgabe ${ausgabeId} as inactive:`,
        error as Error
      );
      throw error;
    }
  }

  /**
   * Clear the cache (useful for testing or explicit refresh)
   */
  static clearCache(): void {
    this.emptyCache.clear();
  }

  /**
   * Clear a specific cache entry
   */
  static clearCacheFor(ausgabeId: number): void {
    this.emptyCache.delete(ausgabeId);
  }
}
